import classNames from 'classnames'
import React, { InputHTMLAttributes, ReactElement } from 'react'
import { FormControl } from '../FormControl/FormControl'
import styles from './Select.module.css'

type SelectState = {
  nextError: string | undefined
}

type BaseProps = {
  label: string
  helpText?: React.ReactNode
  error?: string
}

type SelectProps = InputHTMLAttributes<HTMLSelectElement> & BaseProps & {
  label: string
  name: string
  options: Array<{ value: string, label: string }>
  testId?: string
}
/**
 * Error first
 * Help text second
 * nbsp fallback
 *
 * This way the layout does not change when
 * an error is added
 */
export const getNextHelpText = (
  error: React.ReactNode,
  helpText: React.ReactNode,
): React.ReactNode => (
  error || helpText || '\u00A0'
)

export class Select extends React.Component<SelectProps, SelectState> {
  selectRef: React.RefObject<HTMLSelectElement | null>

  constructor(props: SelectProps) {
    super(props)
    this.state = { nextError: props.error }
    this.selectRef = React.createRef<HTMLSelectElement>()
  }

  handleOnChange(value: string): void {
    const { required } = this.props

    // set error if required with no val
    if (required && !value.length) {
      this.setState((prevState) => ({
        ...prevState,
        nextError: 'Required',
      }))
    } else {
      this.setState((prevState) => ({
        ...prevState,
        nextError: undefined,
      }))
    }
  }

  render(): ReactElement<HTMLElement> {
    const {
      options,
      className,
      disabled,
      helpText,
      error,
      onChange,
      required,
      id,
      label,
      name,
      testId = name,
      ...rest
    } = this.props
    const { nextError } = this.state
    const inputId = id || name

    const nextHelpText = getNextHelpText(error, helpText)

    return (
      <FormControl
        disabled={!!disabled}
        error={!!nextError}
        required={!!required}
        className={classNames(styles.Select, className)}
      >
        <label htmlFor={inputId}>
          {label}
        </label>
        <div>
          <select
            ref={this.selectRef}
            {...rest}
            disabled={disabled}
            data-testid={testId}
            onChange={(evt) => {
              this.handleOnChange(evt.currentTarget.value)
              if (onChange) {
                onChange(evt)
              }
            }}
            required={required}
            aria-label={label}
            name={name}
            id={inputId}
          >
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="helptext">{nextHelpText}</div>
      </FormControl>
    )
  }
}

export default Select
